import { Head, Link } from '@inertiajs/react'
import { useState, useEffect, useRef } from 'react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface EventShowData {
  id: number
  name: string
  about: string | null
  location: string | null
  eventDate: string | null
  eventDateFormatted: string | null
  eventDay: string | null
  eventTime: string | null
  videoLink: string | null
  primaryColor: string
  secondaryColor: string
  font: string | null
  template: number
  noTemplate: boolean | null
  logoUrl: string | null
  backgroundUrl: string | null
  hasSpeakers: boolean
  hasAgenda: boolean
  hasPartners: boolean
  hasExternalLinks: boolean
  hasSocialLinks: boolean
  hasRegistration: boolean
  registrationUrl: string
}

interface SpeakerData {
  id: number
  name: string
  description: string | null
  imageUrl: string | null
}

interface DayItemData {
  description: string
  time: string | null
}

interface DayData {
  number: number
  items: DayItemData[]
}

interface VenueData {
  name: string | null
  location: string | null
  imageUrls: string[]
}

interface SocialLinksData {
  twitter: string | null
  instagram: string | null
  linkedin: string | null
  facebook: string | null
  youtube: string | null
}

interface ExternalLinkData {
  name: string
  link: string
}

interface PartnerData {
  id: number
  name: string
  imageUrl: string | null
}

interface ShowProps {
  event: EventShowData
  speakers: SpeakerData[]
  days: DayData[]
  venue: VenueData | null
  socialLinks: SocialLinksData | null
  externalLinks: ExternalLinkData[]
  partners: PartnerData[]
  galleryUrls: string[]
  locale: string
  translations: Record<string, string>
  otherLocale: string
  otherLocaleName: string
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '')
  return [
    parseInt(h.substring(0, 2), 16) || 0,
    parseInt(h.substring(2, 4), 16) || 0,
    parseInt(h.substring(4, 6), 16) || 0,
  ]
}

function rgba(hex: string, alpha: number) {
  const [r, g, b] = hexToRgb(hex)
  return `rgba(${r},${g},${b},${alpha})`
}

/* ------------------------------------------------------------------ */
/*  Lightbox                                                           */
/* ------------------------------------------------------------------ */

function Lightbox({
  images,
  index,
  onClose,
}: {
  images: string[]
  index: number
  onClose: () => void
}) {
  const [current, setCurrent] = useState(index)

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight') setCurrent((c) => (c + 1) % images.length)
      if (e.key === 'ArrowLeft') setCurrent((c) => (c - 1 + images.length) % images.length)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [images.length, onClose])

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white text-3xl font-light hover:text-gray-300 cursor-pointer z-10"
      >
        ✕
      </button>
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setCurrent((c) => (c - 1 + images.length) % images.length) }}
            className="absolute left-4 text-white text-4xl font-light hover:text-gray-300 cursor-pointer z-10"
          >
            ‹
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setCurrent((c) => (c + 1) % images.length) }}
            className="absolute right-4 text-white text-4xl font-light hover:text-gray-300 cursor-pointer z-10"
          >
            ›
          </button>
        </>
      )}
      <img
        src={images[current]}
        alt=""
        className="max-h-[90vh] max-w-[90vw] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Countdown                                                          */
/* ------------------------------------------------------------------ */

function useCountdown(dateStr: string | null) {
  const [diff, setDiff] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 })

  useEffect(() => {
    if (!dateStr) return
    const target = new Date(dateStr).getTime()
    function update() {
      const now = Date.now()
      const d = Math.max(0, target - now)
      setDiff({
        days: Math.floor(d / 86400000),
        hours: Math.floor((d % 86400000) / 3600000),
        minutes: Math.floor((d % 3600000) / 60000),
        seconds: Math.floor((d % 60000) / 1000),
      })
    }
    update()
    const id = setInterval(update, 1000)
    return () => clearInterval(id)
  }, [dateStr])

  return diff
}

/* ------------------------------------------------------------------ */
/*  Social icons as inline SVG                                         */
/* ------------------------------------------------------------------ */

function TwitterIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  )
}
function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  )
}
function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  )
}
function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  )
}
function YouTubeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  )
}

function SocialIcons({
  social,
  className,
  iconSize = 'h-5 w-5',
}: {
  social: SocialLinksData | null
  className?: string
  iconSize?: string
}) {
  if (!social) return null
  const links = [
    { url: social.twitter, icon: TwitterIcon, label: 'Twitter' },
    { url: social.instagram, icon: InstagramIcon, label: 'Instagram' },
    { url: social.linkedin, icon: LinkedInIcon, label: 'LinkedIn' },
    { url: social.facebook, icon: FacebookIcon, label: 'Facebook' },
    { url: social.youtube, icon: YouTubeIcon, label: 'YouTube' },
  ].filter((l) => l.url)

  if (links.length === 0) return null

  return (
    <div className={`flex items-center gap-3 ${className || ''}`}>
      {links.map((l) => (
        <a key={l.label} href={l.url!} target="_blank" rel="noopener noreferrer" aria-label={l.label}>
          <l.icon className={iconSize} />
        </a>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Shared Nav (smooth scroll)                                         */
/* ------------------------------------------------------------------ */

function scrollTo(id: string) {
  const el = document.getElementById(id)
  if (el) el.scrollIntoView({ behavior: 'smooth' })
}

/* ================================================================== */
/*  TEMPLATE 1 — "TheEvent" (Classic Elegant)                         */
/* ================================================================== */

function Template1(props: ShowProps) {
  const { event: ev, speakers, days, venue, socialLinks, externalLinks, partners, galleryUrls, translations: t, otherLocale, otherLocaleName } = props
  const [lightbox, setLightbox] = useState<{ images: string[]; index: number } | null>(null)
  const [mobileNav, setMobileNav] = useState(false)

  const pc = ev.primaryColor
  const sc = ev.secondaryColor

  const navItems = [
    { id: 'hero', label: t.home },
    { id: 'about', label: t.about },
    ev.hasSpeakers && speakers.length > 0 ? { id: 'speakers', label: t.speakers } : null,
    ev.hasAgenda && days.length > 0 ? { id: 'schedule', label: t.schedule } : null,
    venue ? { id: 'venue', label: t.venue } : null,
    galleryUrls.length > 0 ? { id: 'gallery', label: t.gallery } : null,
    ev.hasPartners && partners.length > 0 ? { id: 'partners', label: t.partners } : null,
  ].filter(Boolean) as { id: string; label: string }[]

  return (
    <div style={{ fontFamily: ev.font || 'inherit' }} className="text-gray-900 bg-white">
      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-50 bg-white/95 backdrop-blur shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <a onClick={() => scrollTo('hero')} className="cursor-pointer flex items-center">
            {ev.logoUrl && <img src={ev.logoUrl} alt={ev.name} className="h-10 object-contain" />}
          </a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6">
            {navItems.map((n) => (
              <a
                key={n.id}
                onClick={() => scrollTo(n.id)}
                className="text-sm font-medium cursor-pointer transition-colors hover:opacity-80"
                style={{ color: sc }}
              >
                {n.label}
              </a>
            ))}
            <a
              href={`?locale=${otherLocale}`}
              className="text-sm font-medium px-3 py-1 rounded-full border transition-colors"
              style={{ borderColor: pc, color: pc }}
            >
              {otherLocaleName}
            </a>
            {ev.hasRegistration && (
              <a
                href={ev.registrationUrl}
                className="text-sm font-medium px-4 py-2 rounded-full text-white transition-opacity hover:opacity-90"
                style={{ background: pc }}
              >
                {t.registration}
              </a>
            )}
          </nav>

          {/* Mobile hamburger */}
          <button onClick={() => setMobileNav(!mobileNav)} className="md:hidden p-2 cursor-pointer" style={{ color: pc }}>
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileNav ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
            </svg>
          </button>
        </div>
        {/* Mobile menu */}
        {mobileNav && (
          <div className="md:hidden bg-white border-t p-4 space-y-3">
            {navItems.map((n) => (
              <a key={n.id} onClick={() => { scrollTo(n.id); setMobileNav(false) }} className="block text-sm font-medium cursor-pointer" style={{ color: sc }}>{n.label}</a>
            ))}
            <a href={`?locale=${otherLocale}`} className="block text-sm font-medium" style={{ color: pc }}>{otherLocaleName}</a>
            {ev.hasRegistration && <a href={ev.registrationUrl} className="block text-sm font-bold text-white text-center rounded-full py-2" style={{ background: pc }}>{t.registration}</a>}
          </div>
        )}
      </header>

      {/* Hero */}
      <section
        id="hero"
        className="relative min-h-screen flex items-center justify-center text-center text-white pt-16"
        style={{ background: ev.backgroundUrl ? `url(${ev.backgroundUrl}) center/cover no-repeat` : pc }}
      >
        <div className="absolute inset-0" style={{ background: rgba(pc, 0.75) }} />
        <div className="relative z-10 max-w-3xl mx-auto px-6 space-y-6">
          {ev.name && <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight">{ev.name}</h1>}
          {ev.location && ev.eventDateFormatted && (
            <p className="text-lg sm:text-xl opacity-90">{ev.location}, {ev.eventDateFormatted}</p>
          )}
          {ev.videoLink && (
            <a href={ev.videoLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 hover:bg-white/30 transition-colors">
              <svg className="h-8 w-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
            </a>
          )}
          <div>
            <a onClick={() => scrollTo('about')} className="inline-block cursor-pointer border-2 border-white/80 px-8 py-3 rounded-full text-white font-medium hover:bg-white/10 transition-colors">
              {t.about}
            </a>
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="py-20 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-6 gap-12">
          <div className="md:col-span-4">
            <h2 className="text-3xl font-bold mb-6" style={{ color: sc }}>{t.about}</h2>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line text-lg">{ev.about}</p>
          </div>
          <div className="md:col-span-1">
            <h3 className="text-lg font-semibold mb-2" style={{ color: sc }}>{t.where}</h3>
            <p className="text-gray-600">{ev.location}</p>
          </div>
          <div className="md:col-span-1">
            <h3 className="text-lg font-semibold mb-2" style={{ color: sc }}>{t.when}</h3>
            {ev.eventDay && <p className="text-gray-600">{ev.eventDay}</p>}
            {ev.eventDateFormatted && <p className="text-gray-600">{ev.eventDateFormatted}</p>}
          </div>
        </div>
      </section>

      {/* Speakers */}
      {ev.hasSpeakers && speakers.length > 0 && (
        <section id="speakers" className="py-20 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold" style={{ color: sc }}>{t.speakers}</h2>
              <div className="mt-2 h-1 w-20 mx-auto rounded" style={{ background: pc }} />
              <p className="mt-4 text-gray-500">{t.someOfOurSpeakers}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {speakers.map((s) => (
                <div key={s.id} className="group relative overflow-hidden rounded-2xl shadow-lg bg-white">
                  {s.imageUrl ? (
                    <img src={s.imageUrl} alt={s.name} className="w-full aspect-[3/4] object-cover" />
                  ) : (
                    <div className="w-full aspect-[3/4] bg-gray-200 flex items-center justify-center">
                      <svg className="h-20 w-20 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 p-6 text-white transition-all" style={{ background: `linear-gradient(transparent, ${rgba(pc, 0.9)})` }}>
                    <h3 className="text-xl font-semibold">{s.name}</h3>
                    {s.description && <p className="mt-1 text-sm opacity-90 line-clamp-3">{s.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Schedule */}
      {ev.hasAgenda && days.length > 0 && <ScheduleTabs days={days} t={t} pc={pc} sc={sc} event={ev} />}

      {/* Venue */}
      {venue && (
        <section id="venue" className="py-20 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold" style={{ color: sc }}>{t.venue}</h2>
              <div className="mt-2 h-1 w-20 mx-auto rounded" style={{ background: pc }} />
              <p className="mt-4 text-gray-500">{t.venueInfo}</p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {venue.location && (
                <div className="rounded-2xl overflow-hidden shadow-lg aspect-video">
                  <iframe src={venue.location} className="w-full h-full border-0" allowFullScreen loading="lazy" />
                </div>
              )}
              <div className={`flex flex-col justify-center ${venue.location ? '' : 'md:col-span-2 text-center'}`}>
                <h3 className="text-2xl font-semibold mb-3" style={{ color: sc }}>{ev.location}</h3>
                {ev.eventDateFormatted && <p className="text-gray-600 text-lg">{ev.eventDateFormatted}</p>}
              </div>
            </div>
            {venue.imageUrls.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
                {venue.imageUrls.map((url, i) => (
                  <img
                    key={i}
                    src={url}
                    alt=""
                    className="rounded-xl aspect-video object-cover cursor-pointer hover:opacity-90 transition-opacity shadow"
                    onClick={() => setLightbox({ images: venue.imageUrls, index: i })}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Gallery */}
      {galleryUrls.length > 0 && (
        <section id="gallery" className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold" style={{ color: sc }}>{t.gallery}</h2>
              <div className="mt-2 h-1 w-20 mx-auto rounded" style={{ background: pc }} />
              <p className="mt-4 text-gray-500">{t.galleryPast}</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {galleryUrls.map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt=""
                  className="rounded-xl aspect-square object-cover cursor-pointer hover:scale-[1.02] transition-transform shadow"
                  onClick={() => setLightbox({ images: galleryUrls, index: i })}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Partners */}
      {ev.hasPartners && partners.length > 0 && (
        <section id="partners" className="py-20 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold" style={{ color: sc }}>{t.ourPartners}</h2>
              <div className="mt-2 h-1 w-20 mx-auto rounded" style={{ background: pc }} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center justify-items-center">
              {partners.map((p) => (
                <div key={p.id} className="flex items-center justify-center p-4">
                  {p.imageUrl ? (
                    <img src={p.imageUrl} alt={p.name} className="max-h-16 object-contain grayscale hover:grayscale-0 transition-all" />
                  ) : (
                    <span className="text-lg font-semibold text-gray-500">{p.name}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Registration CTA */}
      {ev.hasRegistration && (
        <section id="registration" className="py-20 px-6 text-white text-center" style={{ background: `linear-gradient(135deg, ${pc}, ${sc})` }}>
          <div className="max-w-2xl mx-auto space-y-6">
            <h2 className="text-3xl font-bold">{t.registration}</h2>
            <p className="text-lg opacity-90">{t.registerNow}</p>
            <a
              href={ev.registrationUrl}
              className="inline-block px-10 py-4 rounded-full text-lg font-semibold bg-white transition-transform hover:scale-105"
              style={{ color: pc }}
            >
              {t.registerBtn}
            </a>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-16 px-6 text-white" style={{ background: sc }}>
        <div className="max-w-6xl mx-auto grid sm:grid-cols-3 gap-10">
          <div>
            {ev.logoUrl && <img src={ev.logoUrl} alt={ev.name} className="h-12 object-contain mb-4 brightness-0 invert" />}
          </div>
          {ev.hasExternalLinks && externalLinks.length > 0 && (
            <div>
              <h4 className="font-semibold mb-4 border-b-2 pb-2 inline-block" style={{ borderColor: pc }}>{t.relatedLinks}</h4>
              <ul className="space-y-2">
                {externalLinks.map((el, i) => (
                  <li key={i}><a href={el.link} target="_blank" rel="noopener noreferrer" className="hover:underline opacity-80 hover:opacity-100">{el.name}</a></li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex flex-col items-start sm:items-end justify-between">
            <SocialIcons social={socialLinks} className="text-white" iconSize="h-5 w-5" />
          </div>
        </div>
      </footer>

      {lightbox && <Lightbox images={lightbox.images} index={lightbox.index} onClose={() => setLightbox(null)} />}
    </div>
  )
}

/* ================================================================== */
/*  TEMPLATE 2 — "Eventify" (Modern / Gradient)                       */
/* ================================================================== */

function Template2(props: ShowProps) {
  const { event: ev, speakers, days, venue, socialLinks, externalLinks, partners, galleryUrls, translations: t, otherLocale, otherLocaleName } = props
  const [lightbox, setLightbox] = useState<{ images: string[]; index: number } | null>(null)
  const [mobileNav, setMobileNav] = useState(false)
  const countdown = useCountdown(ev.eventDate)

  const pc = ev.primaryColor
  const sc = ev.secondaryColor

  const navItems = [
    { id: 'hero', label: t.home },
    ev.hasAgenda && days.length > 0 ? { id: 'schedule', label: t.schedule } : null,
    ev.hasSpeakers && speakers.length > 0 ? { id: 'speakers', label: t.speakers } : null,
    galleryUrls.length > 0 ? { id: 'gallery', label: t.gallery } : null,
  ].filter(Boolean) as { id: string; label: string }[]

  return (
    <div style={{ fontFamily: ev.font || 'inherit' }} className="text-gray-900 bg-white">
      {/* ← Fixed Nav */}
      <header className="fixed top-0 inset-x-0 z-50 bg-white/95 backdrop-blur shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <a onClick={() => scrollTo('hero')} className="cursor-pointer">
            {ev.logoUrl && <img src={ev.logoUrl} alt={ev.name} className="h-10 object-contain" />}
          </a>
          <nav className="hidden md:flex items-center gap-6">
            {navItems.map((n) => (
              <a key={n.id} onClick={() => scrollTo(n.id)} className="text-sm font-medium cursor-pointer text-gray-700 hover:text-gray-900">{n.label}</a>
            ))}
            <a href={`?locale=${otherLocale}`} className="text-sm font-medium text-gray-700">{otherLocaleName}</a>
            {ev.hasRegistration && (
              <a href={ev.registrationUrl} className="text-sm font-medium px-5 py-2 rounded-full text-white" style={{ background: `linear-gradient(135deg, ${pc}, ${sc})` }}>
                {t.registration}
              </a>
            )}
          </nav>
          <button onClick={() => setMobileNav(!mobileNav)} className="md:hidden p-2 cursor-pointer text-gray-700">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileNav ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
            </svg>
          </button>
        </div>
        {mobileNav && (
          <div className="md:hidden bg-white border-t p-4 space-y-3">
            {navItems.map((n) => (
              <a key={n.id} onClick={() => { scrollTo(n.id); setMobileNav(false) }} className="block text-sm font-medium cursor-pointer text-gray-700">{n.label}</a>
            ))}
            <a href={`?locale=${otherLocale}`} className="block text-sm font-medium text-gray-700">{otherLocaleName}</a>
          </div>
        )}
      </header>

      {/* Hero with countdown */}
      <section
        id="hero"
        className="relative min-h-screen flex items-center justify-center text-white pt-16"
        style={{ background: `linear-gradient(135deg, ${pc} 0%, ${sc} 100%)` }}
      >
        {ev.backgroundUrl && (
          <div
            className="absolute inset-0 opacity-20"
            style={{ background: `url(${ev.backgroundUrl}) center/cover` }}
          />
        )}
        <div className="relative z-10 text-center max-w-4xl mx-auto px-6 space-y-8">
          {/* Countdown */}
          {ev.eventDate && (
            <div className="flex justify-center gap-4 sm:gap-8 mb-6">
              {[
                { value: countdown.days, label: 'Days' },
                { value: countdown.hours, label: 'Hours' },
                { value: countdown.minutes, label: 'Min' },
                { value: countdown.seconds, label: 'Sec' },
              ].map((c) => (
                <div key={c.label} className="flex flex-col items-center">
                  <span className="text-4xl sm:text-5xl md:text-6xl font-bold">{String(c.value).padStart(2, '0')}</span>
                  <span className="text-xs sm:text-sm uppercase tracking-wider opacity-70 mt-1">{c.label}</span>
                </div>
              ))}
            </div>
          )}

          {ev.name && <h1 className="text-3xl sm:text-5xl md:text-6xl font-extrabold leading-tight">{ev.name}</h1>}
          {ev.location && ev.eventDateFormatted && (
            <p className="text-lg sm:text-xl opacity-80">{ev.location} ・ {ev.eventDateFormatted}</p>
          )}
          <div className="flex flex-wrap justify-center gap-4">
            {ev.hasRegistration && (
              <a href={ev.registrationUrl} className="inline-block px-8 py-3 rounded-full bg-white font-semibold transition-transform hover:scale-105" style={{ color: pc }}>
                {t.registerBtn}
              </a>
            )}
            <a onClick={() => scrollTo('about')} className="inline-block cursor-pointer border-2 border-white/50 px-8 py-3 rounded-full font-medium hover:bg-white/10 transition-colors">
              {t.about}
            </a>
          </div>
        </div>

        {/* Wave bottom */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" preserveAspectRatio="none" className="w-full h-16 md:h-24">
            <path d="M0,64 C360,120 1080,0 1440,64 L1440,120 L0,120 Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* About */}
      <section id="about" className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6" style={{ color: sc }}>{t.about}</h2>
          <div className="h-1 w-16 mx-auto rounded mb-8" style={{ background: pc }} />
          <p className="text-gray-600 leading-relaxed text-lg whitespace-pre-line">{ev.about}</p>
          {ev.eventDateFormatted && <p className="mt-6 text-gray-400">{ev.eventDateFormatted}</p>}
        </div>
      </section>

      {/* Schedule */}
      {ev.hasAgenda && days.length > 0 && <ScheduleTabs days={days} t={t} pc={pc} sc={sc} event={ev} bgClass="bg-gray-50" />}

      {/* Speakers */}
      {ev.hasSpeakers && speakers.length > 0 && (
        <section id="speakers" className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold" style={{ color: sc }}>{t.speakers}</h2>
              <div className="mt-2 h-1 w-16 mx-auto rounded" style={{ background: pc }} />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {speakers.map((s) => (
                <div key={s.id} className="text-center group">
                  <div className="w-40 h-40 mx-auto mb-4 rounded-full overflow-hidden shadow-lg ring-4 ring-transparent group-hover:ring-opacity-30 transition-all" style={{ ['--tw-ring-color' as any]: pc }}>
                    {s.imageUrl ? (
                      <img src={s.imageUrl} alt={s.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <svg className="h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                      </div>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold" style={{ color: sc }}>{s.name}</h3>
                  {s.description && <p className="mt-1 text-sm text-gray-500 line-clamp-2">{s.description}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      {galleryUrls.length > 0 && (
        <section id="gallery" className="py-20 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold" style={{ color: sc }}>{t.gallery}</h2>
              <div className="mt-2 h-1 w-16 mx-auto rounded" style={{ background: pc }} />
            </div>
            <div className="columns-2 md:columns-3 gap-4 space-y-4">
              {galleryUrls.map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt=""
                  className="w-full rounded-xl cursor-pointer hover:opacity-90 transition-opacity shadow break-inside-avoid"
                  onClick={() => setLightbox({ images: galleryUrls, index: i })}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Partners */}
      {ev.hasPartners && partners.length > 0 && (
        <section id="partners" className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold" style={{ color: sc }}>{t.ourPartners}</h2>
              <div className="mt-2 h-1 w-16 mx-auto rounded" style={{ background: pc }} />
            </div>
            <div className="flex flex-wrap justify-center gap-12 items-center">
              {partners.map((p) => (
                <div key={p.id} className="flex items-center justify-center">
                  {p.imageUrl ? (
                    <img src={p.imageUrl} alt={p.name} className="max-h-14 object-contain opacity-60 hover:opacity-100 transition-opacity" />
                  ) : (
                    <span className="text-lg font-medium text-gray-400">{p.name}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Venue */}
      {venue && venue.location && (
        <section className="py-20 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold" style={{ color: sc }}>{t.venue}</h2>
              <div className="mt-2 h-1 w-16 mx-auto rounded" style={{ background: pc }} />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg aspect-video max-w-4xl mx-auto">
              <iframe src={venue.location} className="w-full h-full border-0" allowFullScreen loading="lazy" />
            </div>
          </div>
        </section>
      )}

      {/* Registration CTA */}
      {ev.hasRegistration && (
        <section className="py-20 px-6 text-center text-white" style={{ background: `linear-gradient(135deg, ${pc}, ${sc})` }}>
          <div className="max-w-2xl mx-auto space-y-6">
            <h2 className="text-3xl font-bold">{t.registration}</h2>
            <p className="text-lg opacity-90">{t.registerNow}</p>
            <a href={ev.registrationUrl} className="inline-block px-10 py-4 rounded-full text-lg font-semibold bg-white transition-transform hover:scale-105" style={{ color: pc }}>
              {t.registerBtn}
            </a>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-12 px-6 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>{ev.logoUrl && <img src={ev.logoUrl} alt={ev.name} className="h-10 object-contain brightness-0 invert" />}</div>
          <SocialIcons social={socialLinks} className="text-gray-400 hover:[&>a]:text-white" iconSize="h-5 w-5" />
        </div>
      </footer>

      {lightbox && <Lightbox images={lightbox.images} index={lightbox.index} onClose={() => setLightbox(null)} />}
    </div>
  )
}

/* ================================================================== */
/*  TEMPLATE 3 — "Conference" (Bold / Dark Hero)                       */
/* ================================================================== */

function Template3(props: ShowProps) {
  const { event: ev, speakers, days, venue, socialLinks, externalLinks, partners, galleryUrls, translations: t, otherLocale, otherLocaleName } = props
  const [lightbox, setLightbox] = useState<{ images: string[]; index: number } | null>(null)
  const [mobileNav, setMobileNav] = useState(false)
  const countdown = useCountdown(ev.eventDate)

  const pc = ev.primaryColor
  const sc = ev.secondaryColor

  const navItems = [
    { id: 'hero', label: t.home },
    { id: 'about', label: t.about },
    ev.hasSpeakers && speakers.length > 0 ? { id: 'speakers', label: t.speakers } : null,
    ev.hasAgenda && days.length > 0 ? { id: 'schedule', label: t.schedule } : null,
    galleryUrls.length > 0 ? { id: 'gallery', label: t.gallery } : null,
    ev.hasRegistration ? { id: 'registration', label: t.registration } : null,
  ].filter(Boolean) as { id: string; label: string }[]

  return (
    <div style={{ fontFamily: ev.font || 'inherit' }} className="text-gray-900 bg-white">
      {/* Fixed Nav */}
      <header className="fixed top-0 inset-x-0 z-50 bg-gray-950/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <a onClick={() => scrollTo('hero')} className="cursor-pointer">
            {ev.logoUrl && <img src={ev.logoUrl} alt={ev.name} className="h-10 object-contain brightness-0 invert" />}
          </a>
          <nav className="hidden md:flex items-center gap-6">
            {navItems.map((n) => (
              <a key={n.id} onClick={() => scrollTo(n.id)} className="text-sm font-medium cursor-pointer text-gray-300 hover:text-white transition-colors">{n.label}</a>
            ))}
            <a href={`?locale=${otherLocale}`} className="text-sm font-medium text-gray-400 hover:text-white">{otherLocaleName}</a>
          </nav>
          <button onClick={() => setMobileNav(!mobileNav)} className="md:hidden p-2 cursor-pointer text-gray-300">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileNav ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
            </svg>
          </button>
        </div>
        {mobileNav && (
          <div className="md:hidden bg-gray-950 border-t border-gray-800 p-4 space-y-3">
            {navItems.map((n) => (
              <a key={n.id} onClick={() => { scrollTo(n.id); setMobileNav(false) }} className="block text-sm font-medium cursor-pointer text-gray-300">{n.label}</a>
            ))}
            <a href={`?locale=${otherLocale}`} className="block text-sm font-medium text-gray-400">{otherLocaleName}</a>
          </div>
        )}
      </header>

      {/* Hero — dark with large countdown */}
      <section
        id="hero"
        className="relative min-h-screen flex items-center justify-center text-white pt-16"
        style={{ background: '#0a0a0a' }}
      >
        {ev.backgroundUrl && (
          <div className="absolute inset-0 opacity-30" style={{ background: `url(${ev.backgroundUrl}) center/cover` }} />
        )}
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 50%, ${rgba(pc, 0.3)} 100%)` }} />
        <div className="relative z-10 text-center max-w-4xl mx-auto px-6 space-y-8">
          {ev.name && <h1 className="text-4xl sm:text-5xl md:text-7xl font-black leading-tight tracking-tight">{ev.name}</h1>}

          {/* Large countdown */}
          {ev.eventDate && (
            <div className="flex justify-center gap-6 sm:gap-10">
              {[
                { value: countdown.days, label: 'Days' },
                { value: countdown.hours, label: 'Hours' },
                { value: countdown.minutes, label: 'Minutes' },
                { value: countdown.seconds, label: 'Seconds' },
              ].map((c) => (
                <div key={c.label} className="flex flex-col items-center">
                  <div
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center text-3xl sm:text-4xl font-bold"
                    style={{ background: rgba(pc, 0.15), border: `1px solid ${rgba(pc, 0.3)}` }}
                  >
                    {String(c.value).padStart(2, '0')}
                  </div>
                  <span className="text-[10px] sm:text-xs uppercase tracking-widest text-gray-400 mt-2">{c.label}</span>
                </div>
              ))}
            </div>
          )}

          {ev.location && ev.eventDateFormatted && (
            <p className="text-base sm:text-lg text-gray-400">{ev.location} — {ev.eventDateFormatted}</p>
          )}
        </div>
      </section>

      {/* About */}
      <section id="about" className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">{ev.name}</h2>
          <div className="h-1 w-20 mx-auto rounded mb-8" style={{ background: pc }} />
          <p className="text-gray-600 leading-relaxed text-lg whitespace-pre-line">{ev.about}</p>
          {ev.hasRegistration && (
            <a href={ev.registrationUrl} className="inline-block mt-8 px-8 py-3 rounded-full text-white font-semibold transition-transform hover:scale-105" style={{ background: pc }}>
              {t.registerBtn}
            </a>
          )}
        </div>
      </section>

      {/* Speakers — card with overlay */}
      {ev.hasSpeakers && speakers.length > 0 && (
        <section id="speakers" className="py-20 px-6" style={{ background: '#f8f9fa' }}>
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold">{t.speakers}</h2>
              <div className="mt-2 h-1 w-20 mx-auto rounded" style={{ background: pc }} />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {speakers.map((s) => (
                <div key={s.id} className="group relative rounded-2xl overflow-hidden bg-white shadow-md hover:shadow-xl transition-shadow">
                  {s.imageUrl ? (
                    <img src={s.imageUrl} alt={s.name} className="w-full aspect-[4/5] object-cover" />
                  ) : (
                    <div className="w-full aspect-[4/5] bg-gray-200 flex items-center justify-center">
                      <svg className="h-20 w-20 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                    <div className="text-white">
                      <h3 className="text-xl font-bold">{s.name}</h3>
                      {s.description && <p className="mt-1 text-sm opacity-90 line-clamp-3">{s.description}</p>}
                    </div>
                  </div>
                  <div className="p-4 group-hover:opacity-0 transition-opacity">
                    <h3 className="font-semibold">{s.name}</h3>
                    {s.description && <p className="text-sm text-gray-500 mt-1 line-clamp-2">{s.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Schedule */}
      {ev.hasAgenda && days.length > 0 && <ScheduleTabs days={days} t={t} pc={pc} sc={sc} event={ev} />}

      {/* Partners */}
      {ev.hasPartners && partners.length > 0 && (
        <section className="py-20 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold">{t.ourPartners}</h2>
              <div className="mt-2 h-1 w-20 mx-auto rounded" style={{ background: pc }} />
            </div>
            <div className="flex flex-wrap justify-center gap-10 items-center">
              {partners.map((p) => (
                <div key={p.id}>
                  {p.imageUrl ? (
                    <img src={p.imageUrl} alt={p.name} className="max-h-16 object-contain grayscale hover:grayscale-0 transition-all" />
                  ) : (
                    <span className="text-lg font-semibold text-gray-400">{p.name}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Registration */}
      {ev.hasRegistration && (
        <section id="registration" className="relative py-24 px-6 text-center text-white" style={{ background: '#0a0a0a' }}>
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${rgba(pc, 0.2)}, ${rgba(sc, 0.2)})` }} />
          <div className="relative z-10 max-w-2xl mx-auto space-y-6">
            <h2 className="text-3xl font-bold">{t.registration}</h2>
            <p className="text-lg text-gray-400">{t.registerNow}</p>
            <a href={ev.registrationUrl} className="inline-block px-10 py-4 rounded-full text-lg font-bold transition-transform hover:scale-105 text-white" style={{ background: pc }}>
              {t.registerBtn}
            </a>
          </div>
        </section>
      )}

      {/* Gallery */}
      {galleryUrls.length > 0 && (
        <section id="gallery" className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold">{t.gallery}</h2>
              <div className="mt-2 h-1 w-20 mx-auto rounded" style={{ background: pc }} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {galleryUrls.map((url, i) => (
                <div key={i} className="relative group cursor-pointer overflow-hidden rounded-xl" onClick={() => setLightbox({ images: galleryUrls, index: i })}>
                  <img src={url} alt="" className="w-full aspect-square object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Venue */}
      {venue && venue.location && (
        <section className="relative">
          <iframe src={venue.location} className="w-full h-96 border-0" allowFullScreen loading="lazy" />
        </section>
      )}

      {/* Footer */}
      <footer className="py-12 px-6 bg-gray-950 text-white">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>{ev.logoUrl && <img src={ev.logoUrl} alt={ev.name} className="h-10 object-contain brightness-0 invert" />}</div>
          <SocialIcons social={socialLinks} className="text-gray-500 hover:[&>a]:text-white" iconSize="h-5 w-5" />
        </div>
      </footer>

      {lightbox && <Lightbox images={lightbox.images} index={lightbox.index} onClose={() => setLightbox(null)} />}
    </div>
  )
}

/* ================================================================== */
/*  Shared: Schedule Tabs                                              */
/* ================================================================== */

function ScheduleTabs({
  days,
  t,
  pc,
  sc,
  event,
  bgClass,
}: {
  days: DayData[]
  t: Record<string, string>
  pc: string
  sc: string
  event: EventShowData
  bgClass?: string
}) {
  const [activeDay, setActiveDay] = useState(1)

  return (
    <section id="schedule" className={`py-20 px-6 ${bgClass || ''}`}>
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-3xl font-bold" style={{ color: sc }}>{t.eventSchedule}</h2>
          <div className="mt-2 h-1 w-20 mx-auto rounded" style={{ background: pc }} />
          {event.location && event.eventDateFormatted && (
            <p className="mt-4 text-gray-500">{event.location}, {event.eventDateFormatted}</p>
          )}
        </div>

        {/* Day tabs */}
        {days.length > 1 && (
          <div className="flex justify-center gap-2 mb-10 flex-wrap">
            {days.map((d) => (
              <button
                key={d.number}
                onClick={() => setActiveDay(d.number)}
                className="px-6 py-2 rounded-full text-sm font-medium transition-all cursor-pointer"
                style={
                  activeDay === d.number
                    ? { background: pc, color: '#fff' }
                    : { background: '#e5e7eb', color: '#6b7280' }
                }
              >
                {t.day} {d.number}
              </button>
            ))}
          </div>
        )}

        {/* Active day items */}
        {days
          .filter((d) => d.number === activeDay)
          .map((d) => (
            <div key={d.number} className="space-y-4">
              {d.items.map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4 sm:gap-6 p-4 sm:p-5 rounded-xl bg-white shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  {item.time && (
                    <div className="shrink-0 min-w-[80px] text-right">
                      <span className="text-sm font-semibold" style={{ color: pc }}>{item.time}</span>
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="text-gray-800 whitespace-pre-line">{item.description}</p>
                  </div>
                </div>
              ))}
              {d.items.length === 0 && (
                <p className="text-gray-400 text-center py-8">No schedule items for this day.</p>
              )}
            </div>
          ))}
      </div>
    </section>
  )
}

/* ================================================================== */
/*  Main Component                                                     */
/* ================================================================== */

export default function Show(props: ShowProps) {
  const { event } = props
  const dir = props.locale === 'ar' ? 'rtl' : 'ltr'

  // Import Google Font dynamically
  useEffect(() => {
    if (event.font) {
      // Extract font family name (remove quotes, fallback info)
      const fontName = event.font.replace(/['"]/g, '').split(',')[0].trim()
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@300;400;500;600;700;800;900&display=swap`
      document.head.appendChild(link)
      return () => { document.head.removeChild(link) }
    }
  }, [event.font])

  const templateComponent = (() => {
    switch (event.template) {
      case 2:
        return <Template2 {...props} />
      case 3:
        return <Template3 {...props} />
      default:
        return <Template1 {...props} />
    }
  })()

  return (
    <div dir={dir} lang={props.locale}>
      <Head title={event.name} />
      {templateComponent}
    </div>
  )
}
